/**
 * Where a signed-in user belongs.
 *
 * This existed in two places that disagreed: the login page navigated by role, while the
 * GuestRoute guard on /login redirected every non-admin to /account — and because the guard is
 * declarative it re-renders the moment the store says "authenticated", so it always won. A
 * wholesaler signing in was routed correctly for a fraction of a second and then bounced to the
 * customer dashboard.
 *
 * One function, used by both, so they cannot drift apart again.
 */
/**
 * Is this browser on a white-label portal host?
 *
 * /portal-dashboard only exists when the app is running on a portal subdomain. On the main site
 * that route does not resolve, so sending a portal owner there strands them on a blank page —
 * they belong on their normal account area until they visit their own domain.
 */
function onPortalHost(): boolean {
  try {
    const params = new URLSearchParams(window.location.search)
    if (params.get('portal') || sessionStorage.getItem('portal_preview')) return true
  } catch { /* sessionStorage unavailable — fall through to the host check */ }
  const host = window.location.hostname
  for (const base of ['mobileflow.uk', 'siluxmobile.co.uk']) {
    if (host.endsWith(`.${base}`)) {
      const sub = host.slice(0, -(base.length + 1))
      if (sub && sub !== 'www') return true
    }
  }
  return false
}

export function postLoginDestination(user: any): string {
  if (!user) return '/account'
  if (user.is_admin) return '/admin'
  // A wholesaler owns a portal too, so this must be asked BEFORE the portal question —
  // otherwise they land on a reseller dashboard that is empty by design for them.
  if (user.customer_type === 'wholesale') return '/wholesale'
  // Only where that route exists. On the main site they go to their account area, which is
  // what happened before and is a real page.
  if (user.is_portal_admin || user.customer_type === 'white_label') {
    return onPortalHost() ? '/portal-dashboard' : '/account'
  }
  if (user.customer_type === 'reseller') return '/reseller'
  return '/account'
}
